import React, { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { Button, Container, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { ButtonType } from "@components/atoms/Button/types";
import { buttonCategory, getProductListViewData } from "./Utils";
import { useTranslation } from "react-i18next";
import DragList, { DragListRenderItemInfo } from "react-native-draglist";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { ColumnStructure } from "@scenes/searchResults/types";
import { useSortedTabletListData } from "../../hooks/useSortCustomData";
import { ProductCategory } from "@utils/constants";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import ActiveListViewItemComponent from "./ActiveListViewItemComponent";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";

export type FilterProps = {
  productType: string;
  onClose: any;
  onApply: any;
  onReset: any;
  selectedSortingFilters?: Array<string>;
  sectionName?: SearchFilterTypeEnum;
};

const ArrangeListViewScreen = ({
  productType,
  onClose,
  onApply,
  onReset,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
}: FilterProps) => {
  const { user } = useUserInfo();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const theme = useTheme();
  let fromBulkList = false;

  const productListViewData = getProductListViewData(productType);

  const defaultListDataOptions = useSortedTabletListData(
    productType as ProductCategory,
    user?.price_mode ?? 1,
    sectionName,
    fromBulkList
  );

  const listDataOptions =
    productListViewData && productListViewData.length > 0
      ? productListViewData
      : defaultListDataOptions;

  const { bottom } = useSafeAreaInsets();

  const [paramList, setParamList] = useState<ColumnStructure[]>([]);
  const [activeData, setActiveData] =
    useState<ColumnStructure[]>(listDataOptions);

  const renderItem = (info: DragListRenderItemInfo<ColumnStructure>) => {
    const { item, onDragStart, onDragEnd, isActive } = info;

    return (
      <ActiveListViewItemComponent
        item={item}
        isActive={isActive}
        hideColumnOptions={hideColumnOptions}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        activeData={activeData}
      />
    );
  };

  useEffect(() => {
    if (activeData && activeData.length > 0) {
      if (
        paramList.length !== activeData.length ||
        !paramList?.every((val, index) => val === activeData[index])
      ) {
        setParamList(activeData);
      }
    }
  }, [activeData, paramList]);

  const hideColumnOptions = (_item: ColumnStructure) => {
    setActiveData((prevData) =>
      prevData.map((item) => (item.name === _item.name ? _item : item))
    );
  };

  const onReordered = async (fromIndex: number, toIndex: number) => {
    const finalIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
    const copy = [...activeData];
    const removed = copy.splice(fromIndex, 1);

    copy.splice(finalIndex, 0, removed[0]);
    setActiveData(copy);
  };

  return (
    <>
      <Container noPadding={true}>
        <DragList
          data={activeData}
          onReordered={onReordered}
          keyExtractor={(item: ColumnStructure) => item.label}
          renderItem={renderItem}
          style={{ marginBottom: 50, maxHeight: 548, padding: 12 }}
          ListHeaderComponent={() => (
            <>
              {activeData?.length > 0 && (
                <>
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t("ListViewScreen.label.hideColumn.text")}
                  </VDBText>
                  <Spacer size={SpaceType.Double} />
                </>
              )}
            </>
          )}
          ListFooterComponent={() => (
            <>
              <Spacer size={SpaceType.large} />
            </>
          )}
        />
      </Container>
      <ButtonContainer theme={theme} bottom={bottom}>
        <ButtonView>
          <Button
            type={ButtonType.secondary}
            category={buttonCategory(productType)}
            title={t("ListViewScreen.button.reset.text")}
            maxWidth={164}
            onPress={() => {
              onReset(defaultListDataOptions);
              onClose();
            }}
          />

          <Spacer size={SpaceType.Double} />

          <Button
            type={ButtonType.primary}
            category={buttonCategory(productType)}
            title={t("ListViewScreen.button.done.text")}
            maxWidth={164}
            onPress={() => {
              onApply(paramList);
              onClose();
            }}
          />
        </ButtonView>
      </ButtonContainer>
    </>
  );
};

const ButtonView = styled.View`
  width: ${({ theme }) => (theme.isMobile ? "100%" : "343px")};
  flex-direction: row;
`;

const ButtonContainer = styled.View<any>`
  position: absolute;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme["core-01"]};
  padding: ${SpaceType.Double}px;
  border-bottom-left-radius: ${SpaceType.Double}px;
  border-bottom-right-radius: ${SpaceType.Double}px;
  bottom: 0;
  left: 0;
  right: 0;
`;

export default ArrangeListViewScreen;
